// src/components/Admin/SearchHistory.jsx
import React, { useState, useEffect } from "react";
import {
  Search,
  User,
  Calendar,
  FileText,
  Download
} from "lucide-react";

import API_BASE_URL from "../../services/config";

import { authFetch } from "../../auth";

const SearchHistory = () => {
  const [searchHistory, setSearchHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    dateFrom: "",
    dateTo: ""
  });

  useEffect(() => {
    fetchSearchHistory();
  }, []);

  const fetchSearchHistory = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/admin/search-history`,
        { method: "GET" }
      );

      if (!res.ok) {
        throw new Error(
          `Failed to load search history (${res.status})`
        );
      }

      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setSearchHistory(list);
    } catch (err) {
      console.error("Error fetching search history:", err);
      setError(err.message || "Failed to load search history");
      setSearchHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredHistory = searchHistory.filter((entry) => {
    const queryText =
      typeof entry.queryText === "string" ? entry.queryText : "";

    if (
      filters.search &&
      !queryText.toLowerCase().includes(filters.search.toLowerCase())
    ) {
      return false;
    }

    const created =
      entry.createdAt ? new Date(entry.createdAt) : null;

    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom);
      if (created && created < from) {
        return false;
      }
    }

    if (filters.dateTo) {
      const to = new Date(filters.dateTo);
      if (created && created > to) {
        return false;
      }
    }

    return true;
  });

  const exportToCSV = () => {
    if (filteredHistory.length === 0) return;

    const headers = [
      "User",
      "Email",
      "Search Query",
      "Answer Summary",
      "Timestamp"
    ];

    const rows = filteredHistory.map((entry) => {
      const userName =
        (entry.user && entry.user.name) || "Anonymous";
      const userEmail =
        (entry.user && entry.user.email) || "No email";
      const queryText =
        typeof entry.queryText === "string"
          ? entry.queryText
          : "";
      const answerSummary =
        typeof entry.answerSummary === "string"
          ? entry.answerSummary
          : "No answer";

      const timestamp = entry.createdAt
        ? new Date(entry.createdAt).toLocaleString()
        : "Unknown";

      // Basic CSV escaping for commas / quotes
      const esc = (v) =>
        `"${String(v).replace(/"/g, '""')}"`;

      return [
        esc(userName),
        esc(userEmail),
        esc(queryText),
        esc(answerSummary),
        esc(timestamp)
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv"
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download =
      "search-history-" +
      new Date().toISOString().split("T")[0] +
      ".csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Search History
            </h1>
            <p className="text-gray-600 mt-1">
              Monitor what users are searching for scriptural
              guidance
            </p>
          </div>
          <button
            onClick={exportToCSV}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </button>
        </div>
        {error && (
          <div className="mt-3 rounded-md bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Queries
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search within queries..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg text-sm"
                value={filters.search}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    search: e.target.value
                  })
                }
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              From Date
            </label>
            <input
              type="date"
              className="border border-gray-300 rounded-lg px-4 py-2 w-full text-sm"
              value={filters.dateFrom}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  dateFrom: e.target.value
                })
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              To Date
            </label>
            <input
              type="date"
              className="border border-gray-300 rounded-lg px-4 py-2 w-full text-sm"
              value={filters.dateTo}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  dateTo: e.target.value
                })
              }
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={() =>
                setFilters({
                  search: "",
                  dateFrom: "",
                  dateTo: ""
                })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Search History Table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Search Query
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Answer Summary
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Timestamp
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredHistory.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                        <User className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {entry.user?.name || "Anonymous"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {entry.user?.email || "No email"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 font-medium">
                      {entry.queryText}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-600">
                      {entry.answerSummary ? (
                        <div className="flex items-center">
                          <FileText className="h-4 w-4 mr-2 text-green-500" />
                          <span className="line-clamp-2">
                            {entry.answerSummary}
                          </span>
                        </div>
                      ) : (
                        <span className="text-gray-400 italic">
                          No answer generated
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {entry.createdAt
                        ? new Date(
                            entry.createdAt
                          ).toLocaleString()
                        : "Unknown"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty state */}
        {filteredHistory.length === 0 && (
          <div className="text-center py-12">
            <Search className="h-12 w-12 text-gray-400 mx-auto" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              No search history found
            </h3>
            <p className="mt-2 text-gray-600 max-w-md mx-auto">
              {searchHistory.length === 0
                ? "No search queries have been made yet."
                : "No search queries match your filters."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchHistory;
